import React from 'react';
import { format } from 'date-fns';

interface EstimateDetailsProps {
  estimate: {
    estimate_number?: string;
    created_at: string;
    estimate_date?: string;
    title: string;
    use_separate_site_address?: boolean;
    site_street_address?: string;
    site_suburb?: string;
    site_city?: string;
    site_province_state?: string;
    site_postal_code?: string;
  };
  customer: {
    name: string;
    last_name: string;
    customer_type?: 'residential' | 'commercial';
    business_name?: string;
    vat_number?: string;
    business_phone?: string;
    business_email?: string;
    street_address?: string;
    suburb?: string;
    city?: string;
    province_state?: string;
    postal_code?: string;
    cell_numbers?: string[];
    emails?: string[];
  } | null;
  userProfile?: {
    personal_phone_number?: string;
  } | null;
  company?: {
    company_phone_number?: string;
    email?: string;
  } | null;
}

const EstimateDetails: React.FC<EstimateDetailsProps> = ({ estimate, customer, userProfile, company }) => {
  console.log('[EstimateDetails] Customer data:', customer);
  console.log('[EstimateDetails] UserProfile data:', userProfile);
  console.log('[EstimateDetails] Company data:', company);
  console.log('[EstimateDetails] Personal phone from userProfile:', userProfile?.personal_phone_number);
  
  // Format phone number to xxx xxx xxxx format
  const formatPhoneNumber = (phone: string | undefined): string => {
    if (!phone) return 'N/A';
    // Remove any existing spaces or formatting
    const cleaned = phone.replace(/\D/g, '');
    // Format as xxx xxx xxxx
    if (cleaned.length >= 10) {
      return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    }
    return phone; // Return original if not standard length
  };

  // Build address from components with proper formatting
  const buildAddress = (customer: any) => {
    if (!customer) return [];
    
    const addressLines = [];
    
    // Street address on first line
    if (customer.street_address) {
      addressLines.push(customer.street_address);
    }
    
    // Suburb on second line if exists
    if (customer.suburb) {
      addressLines.push(customer.suburb);
    }
    
    // City on third line (without province/state)
    if (customer.city) {
      addressLines.push(customer.city);
    }
    
    // Postal code on its own line
    if (customer.postal_code) {
      addressLines.push(customer.postal_code);
    }
    
    return addressLines;
  };
  
  // Build project location address (single line)
  const buildProjectLocationAddress = () => {
    let addressParts = [];
    
    // Use site address if available and enabled
    if (estimate.use_separate_site_address && estimate.site_street_address) {
      if (estimate.site_street_address) addressParts.push(estimate.site_street_address);
      if (estimate.site_suburb) addressParts.push(estimate.site_suburb);
      if (estimate.site_city) addressParts.push(estimate.site_city);
      if (estimate.site_postal_code) addressParts.push(estimate.site_postal_code);
    } else if (customer) {
      // Use customer address
      if (customer.street_address) addressParts.push(customer.street_address);
      if (customer.suburb) addressParts.push(customer.suburb);
      if (customer.city) addressParts.push(customer.city);
      if (customer.postal_code) addressParts.push(customer.postal_code);
    }
    
    return addressParts.join(', ');
  };

  // Determine if customer is commercial
  const isCommercialCustomer = customer?.customer_type === 'commercial';

  // Get customer display name
  const getCustomerDisplayName = () => {
    if (!customer) return null;
    if (isCommercialCustomer && customer.business_name) {
      return customer.business_name;
    }
    return `${customer.name} ${customer.last_name}`;
  };

  // Get customer contact info
  const getCustomerEmail = () => {
    if (!customer) return 'N/A';
    if (isCommercialCustomer && customer.business_email) {
      return customer.business_email;
    }
    return customer.emails?.length > 0 ? customer.emails[0] : 'N/A';
  };

  const getCustomerPhone = () => {
    if (!customer) return undefined;
    if (isCommercialCustomer && customer.business_phone) {
      return customer.business_phone;
    }
    return customer.cell_numbers?.length > 0 ? customer.cell_numbers[0] : undefined;
  };
  
  // Use site address in place of customer address when the toggle is on
  const getDisplayAddressLines = (): string[] => {
    if (estimate.use_separate_site_address && estimate.site_street_address) {
      return [
        estimate.site_street_address,
        estimate.site_suburb,
        estimate.site_city,
        estimate.site_postal_code,
      ].filter((l): l is string => Boolean(l));
    }
    return buildAddress(customer);
  };

  const addressLines = getDisplayAddressLines();
  const projectLocationAddress = buildProjectLocationAddress();
  
  return (
    <>
      {/* Estimate and Customer Details */}
      <div className="mb-6">
        <div className="space-y-2 text-sm text-gray-700">
          {/* Estimate Reference */}
          <p>
            <span className="font-bold">Estimate Ref:</span> <span className="font-bold">{estimate.estimate_number || 'EST' + format(new Date(estimate.created_at), 'ddMMyy') + '#01'}</span>
          </p>

          {/* Customer Information */}
          {customer ? (
            <>
              <p><span className="font-bold">Client:</span> {getCustomerDisplayName()}</p>
              
              <p>
                <span className="font-bold">Address:</span> {addressLines.length > 0 ? addressLines[0] : 'N/A'}
                {addressLines.length > 1 && (
                  <>
                    {addressLines.slice(1).map((line, index) => (
                      <span key={index}>
                        <br />
                        <span className="ml-16">{line}</span>
                      </span>
                    ))}
                  </>
                )}
              </p>
              
              {/* Show VAT Reg No. for commercial customers - after address */}
              {isCommercialCustomer && customer.vat_number && (
                <p><span className="font-bold">VAT Reg No.:</span> {customer.vat_number}</p>
              )}
              
              <p><span className="font-bold">Phone:</span> {formatPhoneNumber(getCustomerPhone())}</p>
              <p><span className="font-bold">Mail:</span> {getCustomerEmail()}</p>
            </>
          ) : (
            <p className="text-gray-500">No customer specified</p>
          )}
        </div>
      </div>

      {/* Horizontal Line */}
      <div className="border-t border-gray-900 mb-4"></div>

      {/* Project Location and Date with better spacing */}
      <div className="flex justify-between items-start mb-4 gap-4">
        {/* Project Location - can wrap with max width to prevent collision */}
        {projectLocationAddress && (
          <div className="flex-1 pr-4" style={{ maxWidth: '75%' }}>
            <p className="text-sm text-gray-700 font-bold break-words">
              {estimate.title} at {projectLocationAddress}
            </p>
          </div>
        )}
        
        {/* Date - no wrap, stays on right */}
        <div className="flex-shrink-0" style={{ minWidth: '20%' }}>
          <p className="text-sm text-gray-700 font-bold whitespace-nowrap">
            {format(new Date(estimate.estimate_date || estimate.created_at), 'd MMMM yyyy')}
          </p>
        </div>
      </div>
    </>
  );
};

export default EstimateDetails;
